// ============================================================================
// PROJECT TAG CONTEXT - Tag state and tag operations
// ============================================================================
// What it does: Holds tag state (available tags, selected tags for filtering, initialized
// flags) and tag actions: add/clear/toggle tags, init from projects, lazy-load tags from
// API when Create/Edit modal opens. getTabSpecificTags(activeTab) returns tags for
// dropdowns. It does NOT filter the project list—that is done in display derivation
// ProjectDataContext reads selectedTags from here and sends them to the list API (OR match on backend).

import React, { createContext, useState, useContext, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import APIURLMaker from '../../../utils/APIURLMaker';
import { useProjectUtility } from '../../../shared/context/item/ItemUtilityContext';
import { useAccessMapFiltering } from '../../../hooks/accessMap/useAccessMapFiltering';
import { useAuth } from '../../../shared/context/AuthContext';
import { parseTagsBySource } from '../../../shared/utils/parseTagsBySource';

// ============================================================================
// CONTEXT CREATION AND EXPORTS
// ============================================================================

const ProjectTagContext = createContext();

export const useProjectTag = () => useContext(ProjectTagContext);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export const ProjectTagProvider = ({ children }) => {
  // ============================================================================
  // EXTERNAL DEPENDENCIES
  // ============================================================================
  
  // Get current user for filtering
  const { currentUser } = useAuth();
  
  // Get utility functions from ItemUtilityContext
  const { getTagColorClass } = useProjectUtility();
  
  // ============================================================================
  // ACCESS MAP HOOKS
  // ============================================================================
  
  // Use filtering hook for tab-based filtering
  const { filterByTab: filterByTabHook } = useAccessMapFiltering(currentUser?.username, currentUser);

  // ============================================================================
  // TAG STATE MANAGEMENT
  // ============================================================================
  
  // MY-source tags from getProjectTags (member projects) — used in create/edit modals
  const [myTags, setMyTags] = useState([]);
  // PUBLIC-source tags from getProjectTags — discover section in create/edit modals
  const [publicTags, setPublicTags] = useState([]);

  // All available tags that can be used for filtering projects (MY list when loaded from API)
  const [tags, setTags] = useState([]);
  
  // Tags currently selected for filtering projects
  // When tags are selected, only projects with ALL selected tags are shown
  const [selectedTags, setSelectedTags] = useState([]);
  
  // Flag to track if tags have been initialized (page has done first projects fetch)
  const [tagsInitialized, setTagsInitialized] = useState(false);

  // Flag to track if the full tag list has been loaded from GET getProjectTags (for Create/Edit modals)
  const [tagsApiLoaded, setTagsApiLoaded] = useState(false);
  const [tagsApiLoading, setTagsApiLoading] = useState(false);
  const fetchingTagsRef = useRef(false);

  // Projects owned by the current user - used for stable tag list when derived from projects
  const [initialUserProjects, setInitialUserProjects] = useState([]);

  // All projects the user has access to - used by getTabSpecificTags when we have project-derived tags
  const [allAccessibleProjects, setAllAccessibleProjects] = useState([]);

  // ============================================================================
  // TAG MANAGEMENT FUNCTIONS
  // ============================================================================
  
  // Function to update tags from external source (called by ProjectDataContext)
  const updateTags = useCallback((newTags) => {
    setTags(newTags);
  }, []);

  // Function to update selected tags
  const updateSelectedTags = useCallback((newSelectedTags) => {
    setSelectedTags(newSelectedTags);
  }, []);

  const addTagToMyList = useCallback((newTag) => {
    if (!newTag) {
      return;
    }
    setMyTags((prev) => (prev.includes(newTag) ? prev : [...prev, newTag]));
    setTags((prev) => (prev.includes(newTag) ? prev : [...prev, newTag]));
  }, []);

  // Function to add a new tag to the available tags list (MY source for modals)
  const addTag = useCallback((newTag) => {
    addTagToMyList(newTag);
  }, [addTagToMyList]);

  // Function to add multiple tags to the available tags list
  const addTags = useCallback((newTags) => {
    if (Array.isArray(newTags)) {
      newTags.filter(Boolean).forEach((tag) => addTagToMyList(tag));
    }
  }, [addTagToMyList]);

  // Function to clear all selected tags
  const clearSelectedTags = useCallback(() => {
    setSelectedTags([]);
  }, []);

  // Function to toggle a tag selection
  const toggleTagSelection = useCallback((tag) => {
    setSelectedTags(prevSelected => {
      if (prevSelected.includes(tag)) {
        return prevSelected.filter(t => t !== tag);
      } else {
        return [...prevSelected, tag];
      }
    });
  }, []);

  // ============================================================================
  // TAG INITIALIZATION FUNCTIONS
  // ============================================================================
  
  // Function to initialize tags from owned projects
  const initializeTagsFromOwnedProjects = useCallback((ownedProjects) => {
    // Extract unique tags from owned projects
    const uniqueTags = [...new Set(ownedProjects.flatMap(project => project.tags || []))].filter(Boolean);
    setTags(uniqueTags);
    setMyTags(uniqueTags);
    setInitialUserProjects(ownedProjects);
  }, []);

  // Function to update all accessible projects for stable tag list
  const updateAllAccessibleProjects = useCallback((allProjects) => {
    setAllAccessibleProjects(allProjects);
  }, []);

  // Function to mark tags as initialized (used by ProjectDataContext after first projects fetch)
  const markTagsAsInitialized = useCallback(() => {
    setTagsInitialized(true);
  }, []);

  // Load tag list from backend (GET getProjectTags). Used when Create/Edit modal opens.
  // Cached: only fetches once per session; ref prevents duplicate in-flight requests.
  const applyTagsBySource = useCallback(({ my, public: publicList }) => {
    setMyTags(my);
    setPublicTags(publicList);
    setTags(my);
  }, []);

  const fetchTagsFromApi = useCallback(async () => {
    if (tagsApiLoaded || fetchingTagsRef.current) return;
    fetchingTagsRef.current = true;
    setTagsApiLoading(true);
    try {
      const url = APIURLMaker("projects/getProjectTags");
      const response = await axios.get(url);
      const parsed = parseTagsBySource(response.data);
      applyTagsBySource(parsed);
      setTagsApiLoaded(true);
    } catch (err) {
      console.error("Error fetching project tags:", err);
      applyTagsBySource({ my: [], public: [] });
      setTagsApiLoaded(true);
    } finally {
      setTagsApiLoading(false);
      fetchingTagsRef.current = false;
    }
  }, [tagsApiLoaded, applyTagsBySource]);

  // Call when Create or Edit modal opens so available tags are loaded once needed.
  const ensureTagsLoaded = useCallback(async () => {
    if (!tagsApiLoaded) {
      await fetchTagsFromApi();
    }
  }, [tagsApiLoaded, fetchTagsFromApi]);

  // ============================================================================
  // TAB-SPECIFIC TAG FUNCTIONS
  // ============================================================================

  // Returns available tags for dropdowns (Create/Edit modals). When we have no project-derived
  // list (allAccessibleProjects empty), returns the API tag list (tags). Otherwise derives from projects.
  const getMyTags = useCallback(() => myTags, [myTags]);

  const getDiscoverableTags = useCallback(() => publicTags, [publicTags]);

  const getTabSpecificTags = useCallback((activeTab) => {
    if (allAccessibleProjects.length === 0) {
      return myTags;
    }
    let relevantProjects = [...allAccessibleProjects];
    if (activeTab) {
      relevantProjects = filterByTabHook(relevantProjects, activeTab);
    }
    relevantProjects = relevantProjects.filter(project => project.status !== "DELETED");
    const uniqueTags = [...new Set(relevantProjects.flatMap(project => project.tags || []))].filter(Boolean);
    return uniqueTags;
  }, [allAccessibleProjects, filterByTabHook, myTags]);

  const isTagsApiLoading = useCallback(
    () => tagsApiLoading && !tagsApiLoaded,
    [tagsApiLoading, tagsApiLoaded]
  );

  // ============================================================================
  // CONTEXT VALUE CREATION
  // ============================================================================
  
  // Create the context value that will be provided to all child components
  const contextValue = useMemo(() => ({
    // ============================================================================
    // TAG STATE
    // ============================================================================
    tags,                              // MY tags (filter sidebar compatibility)
    myTags,
    publicTags,
    selectedTags,                      // Currently selected tags for filtering
    tagsInitialized,                   // Whether tags have been initialized
    tagsApiLoading,
    initialUserProjects,               // Projects owned by current user
    allAccessibleProjects,             // All projects user has access to
    
    // ============================================================================
    // TAG MANAGEMENT FUNCTIONS
    // ============================================================================
    setTags: updateTags,               // Function to update available tags
    setSelectedTags: updateSelectedTags, // Function to update selected tags
    addTag,                            // Function to add a single tag
    addTags,                           // Function to add multiple tags
    clearSelectedTags,                 // Function to clear all selected tags
    toggleTagSelection,                // Function to toggle tag selection
    
    // ============================================================================
    // TAG INITIALIZATION FUNCTIONS
    // ============================================================================
    initializeTagsFromOwnedProjects,   // Function to initialize tags from owned projects
    updateAllAccessibleProjects,       // Function to update all accessible projects
    markTagsAsInitialized,             // Function to mark tags as initialized
    ensureTagsLoaded,                  // Load tags from API when Create/Edit modal opens (lazy)
    fetchTagsFromApi,                  // Direct fetch (used by ensureTagsLoaded)

    // ============================================================================
    // TAG UTILITY FUNCTIONS
    // ============================================================================
    getMyTags,
    getDiscoverableTags,
    getTabSpecificTags,                // Function to get tags for current tab
    isTagsApiLoading,
    getTagColorClass,                  // Function to get CSS class for tags
  }), [
    tags,
    myTags,
    publicTags,
    selectedTags,
    tagsInitialized,
    tagsApiLoaded,
    tagsApiLoading,
    initialUserProjects,
    allAccessibleProjects,
    updateTags,
    updateSelectedTags,
    addTag,
    addTags,
    clearSelectedTags,
    toggleTagSelection,
    initializeTagsFromOwnedProjects,
    updateAllAccessibleProjects,
    markTagsAsInitialized,
    ensureTagsLoaded,
    fetchTagsFromApi,
    getMyTags,
    getDiscoverableTags,
    getTabSpecificTags,
    isTagsApiLoading,
    getTagColorClass,
  ]);

  return (
    <ProjectTagContext.Provider value={contextValue}>
      {children}
    </ProjectTagContext.Provider>
  );
};
